import Controller from '@ember/controller';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { tracked, cached } from '@glimmer/tracking';
import { query } from '@warp-drive/utilities/json-api';
import type { Store } from '@warp-drive/core';
import type { PaginationMeta } from '../../../utils/api-types.ts';
import type { ItemStats } from '../../../schemas/item-stats.ts';
import type { Recipe } from '../../../schemas/recipe.ts';
import { chunkArray } from '../../../utils/chunk-array.ts';

export default class ItemsIndexResultsController extends Controller {
  queryParams = ['type', 'q', 'sort'];

  @service declare store: Store;

  @tracked items: ItemStats[] = [];
  @tracked recipes: Recipe[] = [];
  @tracked meta: PaginationMeta = { page: 1, totalPages: 1 };
  @tracked isLoadingMore = false;
  @tracked _lastFetchKey = '';
  @tracked columnCount = 1;

  @tracked type = '';
  @tracked q = '';
  @tracked sort = '-createdAt';

  get isRecipeMode() {
    return this.type === 'recipe';
  }

  get hasMore() {
    return this.meta.page < this.meta.totalPages;
  }

  readonly gridBreakpoints: [number, number][] = [
    [1024, 3],
    [640, 2],
    [1, 1],
  ];

  @cached
  get rows(): ItemStats[][] {
    return chunkArray(this.items, this.columnCount);
  }

  @cached
  get recipeRows(): Recipe[][] {
    return chunkArray(this.recipes, this.columnCount);
  }

  @action
  setColumnCount(n: number) {
    this.columnCount = n;
  }

  get gridRowClass(): string {
    if (this.columnCount === 3) return 'mb-6 grid grid-cols-3 gap-6';
    if (this.columnCount === 2) return 'mb-6 grid grid-cols-2 gap-6';
    return 'mb-6 grid grid-cols-1 gap-6';
  }

  @action
  async loadMore() {
    if (this.isLoadingMore || !this.hasMore) return;

    this.isLoadingMore = true;
    try {
      const { items, recipes, meta } = await this.fetchPage(this.meta.page + 1);
      this.items = [...this.items, ...items];
      this.recipes = [...this.recipes, ...recipes];
      this.meta = meta;
    } finally {
      this.isLoadingMore = false;
    }
  }

  @action
  async refresh() {
    if (this.isLoadingMore) return;

    const { items, recipes, meta } = await this.fetchPage(1);
    this.items = items;
    this.recipes = recipes;
    this.meta = meta;
  }

  private async fetchPage(
    page: number
  ): Promise<{ items: ItemStats[]; recipes: Recipe[]; meta: PaginationMeta }> {
    const queryParams: Record<string, string> = {
      'page[number]': page.toString(),
      'page[size]': '24',
      sort: this.sort || '-createdAt',
    };

    if (this.isRecipeMode) {
      if (this.q) queryParams.q = this.q;
      const result = await this.store.request(query('recipe', queryParams));
      const json = result.content as unknown as {
        data?: Recipe[];
        meta?: PaginationMeta;
      };
      return {
        items: [],
        recipes: json.data ?? [],
        meta: json.meta ?? { page: 1, totalPages: 1 },
      };
    }

    if (this.type) queryParams['filter[type]'] = this.type;
    if (this.q) queryParams.q = this.q;

    const result = await this.store.request(
      query('item-stat', queryParams, { resourcePath: 'items/stats' })
    );

    const json = result.content as unknown as {
      data?: ItemStats[];
      meta?: PaginationMeta;
    };

    return {
      items: json.data ?? [],
      recipes: [],
      meta: json.meta ?? { page: 1, totalPages: 1 },
    };
  }
}
